import type { Country, CountryIsoCode } from './types';

/**
 * Country reference set, ISO 3166-1 alpha-2. Curated to the ~50 markets the
 * suite is most likely to encounter — full EU + UK + IE + EFTA, the major
 * Anglosphere countries, top LatAm + APAC + ME markets. Add more entries as
 * customers land in new regions.
 *
 * The `default*` fields drive {@link applyCountryDefaults}: picking a country
 * pre-fills empty regional/currency/timezone fields. Defaults are *suggestions*
 * — the page-level helper never overwrites a user's explicit choice.
 *
 * Multi-language / multi-currency countries (CH, BE, ZA, etc.) get the most
 * common business default; users can pick a different option after.
 */
export const COUNTRIES: readonly Country[] = [
  // ── British Isles + Iceland ───────────────────────────────────────────
  {
    isoCode: 'IE',
    name: 'Ireland',
    flagEmoji: '🇮🇪',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Dublin',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'XI',
    name: 'Northern Ireland',
    flagEmoji: '🇽🇮',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'GBP',
    defaultTimeZone: 'Europe/Dublin',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'GB',
    name: 'Great Britain',
    flagEmoji: '🇬🇧',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'GBP',
    defaultTimeZone: 'Europe/London',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'IS',
    name: 'Iceland',
    flagEmoji: '🇮🇸',
    defaultLanguageCode: 'is',
    defaultCurrencyIsoCode: 'ISK',
    defaultTimeZone: 'Atlantic/Reykjavik',
    defaultDatePattern: 'd.M.yyyy',
    defaultTimePattern: 'HH:mm',
  },

  // ── Eurozone ──────────────────────────────────────────────────────────
  {
    isoCode: 'AT',
    name: 'Austria',
    flagEmoji: '🇦🇹',
    defaultLanguageCode: 'de',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Vienna',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'BE',
    name: 'Belgium',
    flagEmoji: '🇧🇪',
    defaultLanguageCode: 'nl',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Brussels',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'DE',
    name: 'Germany',
    flagEmoji: '🇩🇪',
    defaultLanguageCode: 'de',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Berlin',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'ES',
    name: 'Spain',
    flagEmoji: '🇪🇸',
    defaultLanguageCode: 'es',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Madrid',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'FI',
    name: 'Finland',
    flagEmoji: '🇫🇮',
    defaultLanguageCode: 'fi',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Helsinki',
    defaultDatePattern: 'd.M.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'FR',
    name: 'France',
    flagEmoji: '🇫🇷',
    defaultLanguageCode: 'fr',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Paris',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'GR',
    name: 'Greece',
    flagEmoji: '🇬🇷',
    defaultLanguageCode: 'el',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Athens',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'IT',
    name: 'Italy',
    flagEmoji: '🇮🇹',
    defaultLanguageCode: 'it',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Rome',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'NL',
    name: 'Netherlands',
    flagEmoji: '🇳🇱',
    defaultLanguageCode: 'nl',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Amsterdam',
    defaultDatePattern: 'dd-MM-yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'PT',
    name: 'Portugal',
    flagEmoji: '🇵🇹',
    defaultLanguageCode: 'pt',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Lisbon',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },

  // ── Rest of Europe ────────────────────────────────────────────────────
  {
    isoCode: 'CH',
    name: 'Switzerland',
    flagEmoji: '🇨🇭',
    defaultLanguageCode: 'de',
    defaultCurrencyIsoCode: 'CHF',
    defaultTimeZone: 'Europe/Zurich',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'CZ',
    name: 'Czechia',
    flagEmoji: '🇨🇿',
    defaultLanguageCode: 'cs',
    defaultCurrencyIsoCode: 'CZK',
    defaultTimeZone: 'Europe/Prague',
    defaultDatePattern: 'd.M.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'DK',
    name: 'Denmark',
    flagEmoji: '🇩🇰',
    defaultLanguageCode: 'da',
    defaultCurrencyIsoCode: 'DKK',
    defaultTimeZone: 'Europe/Copenhagen',
    defaultDatePattern: 'dd-MM-yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'HU',
    name: 'Hungary',
    flagEmoji: '🇭🇺',
    defaultLanguageCode: 'hu',
    defaultCurrencyIsoCode: 'HUF',
    defaultTimeZone: 'Europe/Budapest',
    defaultDatePattern: 'yyyy.MM.dd',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'NO',
    name: 'Norway',
    flagEmoji: '🇳🇴',
    defaultLanguageCode: 'no',
    defaultCurrencyIsoCode: 'NOK',
    defaultTimeZone: 'Europe/Oslo',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'PL',
    name: 'Poland',
    flagEmoji: '🇵🇱',
    defaultLanguageCode: 'pl',
    defaultCurrencyIsoCode: 'PLN',
    defaultTimeZone: 'Europe/Warsaw',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'RO',
    name: 'Romania',
    flagEmoji: '🇷🇴',
    defaultLanguageCode: 'ro',
    defaultCurrencyIsoCode: 'RON',
    defaultTimeZone: 'Europe/Bucharest',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'RU',
    name: 'Russia',
    flagEmoji: '🇷🇺',
    defaultLanguageCode: 'ru',
    defaultCurrencyIsoCode: 'RUB',
    defaultTimeZone: 'Europe/Moscow',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'SE',
    name: 'Sweden',
    flagEmoji: '🇸🇪',
    defaultLanguageCode: 'sv',
    defaultCurrencyIsoCode: 'SEK',
    defaultTimeZone: 'Europe/Stockholm',
    defaultDatePattern: 'yyyy-MM-dd',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'TR',
    name: 'Turkey',
    flagEmoji: '🇹🇷',
    defaultLanguageCode: 'tr',
    defaultCurrencyIsoCode: 'TRY',
    defaultTimeZone: 'Europe/Istanbul',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'UA',
    name: 'Ukraine',
    flagEmoji: '🇺🇦',
    defaultLanguageCode: 'uk',
    defaultCurrencyIsoCode: 'EUR',
    defaultTimeZone: 'Europe/Kyiv',
    defaultDatePattern: 'dd.MM.yyyy',
    defaultTimePattern: 'HH:mm',
  },

  // ── Anglosphere ───────────────────────────────────────────────────────
  {
    isoCode: 'AU',
    name: 'Australia',
    flagEmoji: '🇦🇺',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'AUD',
    defaultTimeZone: 'Australia/Sydney',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'CA',
    name: 'Canada',
    flagEmoji: '🇨🇦',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'CAD',
    defaultTimeZone: 'America/Toronto',
    defaultDatePattern: 'yyyy-MM-dd',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'NZ',
    name: 'New Zealand',
    flagEmoji: '🇳🇿',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'NZD',
    defaultTimeZone: 'Pacific/Auckland',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'US',
    name: 'United States',
    flagEmoji: '🇺🇸',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'USD',
    defaultTimeZone: 'America/New_York',
    defaultDatePattern: 'MM/dd/yyyy',
    defaultTimePattern: 'hh:mm a',
  },

  // ── Latin America ─────────────────────────────────────────────────────
  {
    isoCode: 'AR',
    name: 'Argentina',
    flagEmoji: '🇦🇷',
    defaultLanguageCode: 'es',
    defaultCurrencyIsoCode: 'ARS',
    defaultTimeZone: 'America/Argentina/Buenos_Aires',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'BR',
    name: 'Brazil',
    flagEmoji: '🇧🇷',
    defaultLanguageCode: 'pt',
    defaultCurrencyIsoCode: 'BRL',
    defaultTimeZone: 'America/Sao_Paulo',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'CL',
    name: 'Chile',
    flagEmoji: '🇨🇱',
    defaultLanguageCode: 'es',
    defaultCurrencyIsoCode: 'CLP',
    defaultTimeZone: 'America/Santiago',
    defaultDatePattern: 'dd-MM-yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'CO',
    name: 'Colombia',
    flagEmoji: '🇨🇴',
    defaultLanguageCode: 'es',
    defaultCurrencyIsoCode: 'COP',
    defaultTimeZone: 'America/Bogota',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'MX',
    name: 'Mexico',
    flagEmoji: '🇲🇽',
    defaultLanguageCode: 'es',
    defaultCurrencyIsoCode: 'MXN',
    defaultTimeZone: 'America/Mexico_City',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'PE',
    name: 'Peru',
    flagEmoji: '🇵🇪',
    defaultLanguageCode: 'es',
    defaultCurrencyIsoCode: 'USD',
    defaultTimeZone: 'America/Lima',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },

  // ── Middle East / Africa ──────────────────────────────────────────────
  {
    isoCode: 'AE',
    name: 'United Arab Emirates',
    flagEmoji: '🇦🇪',
    defaultLanguageCode: 'ar',
    defaultCurrencyIsoCode: 'AED',
    defaultTimeZone: 'Asia/Dubai',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'EG',
    name: 'Egypt',
    flagEmoji: '🇪🇬',
    defaultLanguageCode: 'ar',
    defaultCurrencyIsoCode: 'USD',
    defaultTimeZone: 'Africa/Cairo',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'IL',
    name: 'Israel',
    flagEmoji: '🇮🇱',
    defaultLanguageCode: 'he',
    defaultCurrencyIsoCode: 'ILS',
    defaultTimeZone: 'Asia/Jerusalem',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'KE',
    name: 'Kenya',
    flagEmoji: '🇰🇪',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'USD',
    defaultTimeZone: 'Africa/Nairobi',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'NG',
    name: 'Nigeria',
    flagEmoji: '🇳🇬',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'USD',
    defaultTimeZone: 'Africa/Lagos',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'SA',
    name: 'Saudi Arabia',
    flagEmoji: '🇸🇦',
    defaultLanguageCode: 'ar',
    defaultCurrencyIsoCode: 'SAR',
    defaultTimeZone: 'Asia/Riyadh',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'ZA',
    name: 'South Africa',
    flagEmoji: '🇿🇦',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'ZAR',
    defaultTimeZone: 'Africa/Johannesburg',
    defaultDatePattern: 'yyyy/MM/dd',
    defaultTimePattern: 'HH:mm',
  },

  // ── Asia / Pacific ────────────────────────────────────────────────────
  {
    isoCode: 'CN',
    name: 'China',
    flagEmoji: '🇨🇳',
    defaultLanguageCode: 'zh',
    defaultCurrencyIsoCode: 'CNY',
    defaultTimeZone: 'Asia/Shanghai',
    defaultDatePattern: 'yyyy-MM-dd',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'HK',
    name: 'Hong Kong',
    flagEmoji: '🇭🇰',
    defaultLanguageCode: 'zh',
    defaultCurrencyIsoCode: 'HKD',
    defaultTimeZone: 'Asia/Hong_Kong',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'ID',
    name: 'Indonesia',
    flagEmoji: '🇮🇩',
    defaultLanguageCode: 'id',
    defaultCurrencyIsoCode: 'IDR',
    defaultTimeZone: 'Asia/Jakarta',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'IN',
    name: 'India',
    flagEmoji: '🇮🇳',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'INR',
    defaultTimeZone: 'Asia/Kolkata',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'JP',
    name: 'Japan',
    flagEmoji: '🇯🇵',
    defaultLanguageCode: 'ja',
    defaultCurrencyIsoCode: 'JPY',
    defaultTimeZone: 'Asia/Tokyo',
    defaultDatePattern: 'yyyy/MM/dd',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'KR',
    name: 'South Korea',
    flagEmoji: '🇰🇷',
    defaultLanguageCode: 'ko',
    defaultCurrencyIsoCode: 'KRW',
    defaultTimeZone: 'Asia/Seoul',
    defaultDatePattern: 'yyyy.MM.dd',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'MY',
    name: 'Malaysia',
    flagEmoji: '🇲🇾',
    defaultLanguageCode: 'ms',
    defaultCurrencyIsoCode: 'MYR',
    defaultTimeZone: 'Asia/Kuala_Lumpur',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'PH',
    name: 'Philippines',
    flagEmoji: '🇵🇭',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'PHP',
    defaultTimeZone: 'Asia/Manila',
    defaultDatePattern: 'MM/dd/yyyy',
    defaultTimePattern: 'hh:mm a',
  },
  {
    isoCode: 'SG',
    name: 'Singapore',
    flagEmoji: '🇸🇬',
    defaultLanguageCode: 'en',
    defaultCurrencyIsoCode: 'SGD',
    defaultTimeZone: 'Asia/Singapore',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'TH',
    name: 'Thailand',
    flagEmoji: '🇹🇭',
    defaultLanguageCode: 'th',
    defaultCurrencyIsoCode: 'THB',
    defaultTimeZone: 'Asia/Bangkok',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
  {
    isoCode: 'VN',
    name: 'Vietnam',
    flagEmoji: '🇻🇳',
    defaultLanguageCode: 'vi',
    defaultCurrencyIsoCode: 'VND',
    defaultTimeZone: 'Asia/Ho_Chi_Minh',
    defaultDatePattern: 'dd/MM/yyyy',
    defaultTimePattern: 'HH:mm',
  },
];

const COUNTRIES_BY_CODE: Map<CountryIsoCode, Country> = new Map(
  COUNTRIES.map((c) => [c.isoCode, c]),
);

/**
 * Look up a country by ISO 3166-1 alpha-2 code (case-insensitive).
 * Returns `undefined` for unknown codes — callers usually treat that as
 * "no defaults to apply" rather than an error.
 */
export function findCountry(isoCode: CountryIsoCode): Country | undefined {
  return COUNTRIES_BY_CODE.get(isoCode.toUpperCase());
}
